import { useRef, useEffect } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "motion/react";
import Hls from "hls.js";

const HLS_URL = "https://stream.mux.com/NcU3HlHeF7CUL86azTTzpy3Tlb00d6iF3BmCdFslMJYM.m3u8";

const stats = [
  { value: 200, suffix: "+", label: "Projects delivered" },
  { value: 98, suffix: "%", label: "Client satisfaction" },
  { value: 3.2, suffix: "x", label: "More conversions", isFloat: true },
  { value: 5, suffix: " days", label: "Average delivery" },
];

function AnimatedNumber({ value, isFloat = false }) {
  const ref = useRef(null);
  // Use amount: "some" for better reliability with scroll-triggered animations
  const isInView = useInView(ref, { once: false, amount: "some" });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (latest) => 
    isFloat ? latest.toFixed(1) : Math.round(latest)
  );

  useEffect(() => {
    if (isInView) {
      const controls = animate(motionValue, value, {
        duration: 2,
        ease: "easeOut",
      });
      return controls.stop;
    } else {
      // Ensure it resets to 0 when out of view so it can animate up again
      motionValue.set(0);
    }
  }, [isInView, value, motionValue]);

  return <motion.span ref={ref} className="tabular-nums">{rounded}</motion.span>;
}

export default function Stats() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(HLS_URL);
      hls.attachMedia(video);
      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = HLS_URL;
    }
  }, []);

  return (
    <section className="relative overflow-hidden py-32" data-testid="stats-section">
      {/* HLS Video Background (desaturated) */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ filter: "saturate(0)" }}
      />

      {/* Top gradient */}
      <div
        className="absolute top-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: "200px",
          background: "linear-gradient(to top, transparent, black)",
        }}
      />

      {/* Bottom gradient */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: "200px",
          background: "linear-gradient(to bottom, transparent, black)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 px-4 sm:px-8 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6 }}
            className="liquid-glass rounded-3xl p-8 md:p-16"
            data-testid="stats-card"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {stats.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`stat-${i + 1}`}
                >
                  <div className="text-4xl md:text-5xl lg:text-6xl font-heading italic text-white flex items-center justify-center">
                    <AnimatedNumber value={s.value} isFloat={s.isFloat} />
                    <span>{s.suffix}</span>
                  </div>
                  <div className="text-white/60 font-body font-light text-sm mt-2">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
